// =============================================================================
// SIREPPAR - Modal "Publicar atributos" (Art. 10 Ley XII N°23)
// =============================================================================
// Lo abre el SECRETARIO: la SEP verifica los atributos y se los eleva, pero la
// publicación para oposiciones es un acto suyo. El cuerpo del edicto viene
// precargado y es editable.
//  - "Generar borrador": genera y descarga el PDF SIN publicar (no toca la base,
//    no cambia la fase, no notifica).
//  - "Publicar": genera, descarga, publica los atributos (fase ATRIBUTOS_A_PUBLICAR
//    → ATRIBUTOS_PUBLICADOS), guarda el PDF como documento y notifica al partido.
//
// El panel lateral de antecedentes deja adjuntar al edicto las intimaciones y sus
// respuestas (mismo mecanismo que los demás informes). El aviso al partido viene
// TILDADO: el edicto se publica para todos, y al partido además se le hace llegar.
// =============================================================================
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from './Modal';
import RichTextEditor from './RichTextEditor';
import { api } from '../../services/api';
import PanelHistorialInforme from './PanelHistorialInforme';
import { aplicarAntecedentes, ItemHistorialInforme } from '../../utils/historialInforme';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  expedienteId: string;
  textoInicial: string;
  onPublicado?: () => void;
}

export default function ModalPublicarAtributos({ isOpen, onClose, expedienteId, textoInicial, onPublicado }: Props) {
  const [texto, setTexto] = useState(textoInicial);
  const [generando, setGenerando] = useState(false);
  const [avisarPartido, setAvisarPartido] = useState(true);

  // Al abrir se recarga el texto: el precargado depende del partido y del
  // expediente, que pueden haber cambiado desde el último render.
  useEffect(() => {
    if (isOpen) {
      setTexto(textoInicial);
      setAvisarPartido(true);
    }
  }, [isOpen, textoInicial]);

  // Los antecedentes tildados se vuelcan como bloque al final del cuerpo, sin pisar
  // lo redactado arriba (es idempotente).
  const aplicarSeleccion = (items: ItemHistorialInforme[]) => {
    setTexto((cuerpo) => aplicarAntecedentes(items, cuerpo));
  };

  const descargarPdf = (pdfBase64: string, filename: string) => {
    const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'publicacion-atributos.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generar = async (persistir: boolean) => {
    if (!texto.trim()) {
      toast.error('Redactá el cuerpo de la publicación');
      return;
    }
    if (persistir && !window.confirm('¿Publicar los atributos para oposición? Corre el plazo de 5 días corridos.')) {
      return;
    }
    setGenerando(true);
    try {
      const res = await api.post(`/reconocimiento/${expedienteId}/publicar-atributos`, {
        texto: texto.trim(),
        persistir,
        notificarPartido: avisarPartido,
      });
      descargarPdf(res.data.data.pdfBase64, res.data.data.filename);
      toast.success(persistir ? 'Atributos publicados' : 'Borrador generado');
      if (persistir) {
        onPublicado?.();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al generar la publicación');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Publicar atributos para oposición" size="full">
      <div className="space-y-5">
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          Redacte el cuerpo de la publicación (Art. 10, Ley XII N° 23). El PDF lista siempre
          los datos del partido (sigla, colores, símbolos), adjunta los símbolos verificados y
          agrega al pie el plazo de oposición de cinco (5) días, la casilla donde se observa y
          dónde se publica. Va firmado por el Secretario Electoral.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cuerpo de la publicación
            </label>
            <RichTextEditor
              value={texto}
              onChange={(v) => setTexto(v ?? '')}
              minHeight={240}
              placeholder="Texto del edicto de publicación de la reserva de atributos…"
            />
          </div>

          {isOpen && (
            <PanelHistorialInforme
              expedienteId={expedienteId}
              resetKey={isOpen}
              onSeleccion={aplicarSeleccion}
            />
          )}
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-lg p-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4"
            checked={avisarPartido}
            onChange={(e) => setAvisarPartido(e.target.checked)}
          />
          <span>
            Enviar la publicación al partido por correo
            <span className="block text-xs text-gray-500">
              Se le adjunta el PDF publicado. La publicación en el sitio web de la SEP se hace igual.
            </span>
          </span>
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => generar(false)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Generando…' : '📄 Generar borrador'}
          </button>
          <button
            type="button"
            onClick={() => generar(true)}
            disabled={generando}
            className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Procesando…' : '✅ Publicar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
